/**
 * CameraFrame component.
 *
 * Phase 3 implementation: renders a video element for known clip URLs (.mp4 or
 * Spaces-hosted), or a placeholder with the VideoCamera Phosphor icon when
 * no URL is available.
 *
 * Phase 3 scope note: camera clip embedding is basic. Plan 03-07 polishes with
 * scrubbing, timeline sync, and error recovery.
 *
 * @param cameraUrl - Presigned Spaces URL or null
 */
import { VideoCameraSlash } from '@phosphor-icons/react';
import * as styles from './StoryCard.css';

interface Props {
  /** Camera clip URL (mp4 or Spaces-hosted). Null when unavailable. */
  cameraUrl: string | null;
}

/**
 * Video container extensions the <video> element can be expected to play.
 * Lowercased; checked against the URL pathname (query string excluded by
 * the URL parser, so presigned URLs with long signatures still match).
 */
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.m4v'] as const;

/**
 * Checks if the URL appears to be a video source we can embed.
 *
 * Generalized 2026-06-10 (internal-cloud readiness): the original check was
 * `.mp4` extension OR a `.digitaloceanspaces.com` hostname, which made the
 * "is this a video" decision storage-provider-specific - an internal MinIO
 * presigned clip URL whose object key lacked a video extension rendered the
 * "Camera clip unavailable" placeholder instead of the video. Detection is
 * now endpoint-agnostic, in priority order:
 *
 *   1. Pathname carries a known video extension (any host, any storage).
 *   2. The DO Spaces hostnames (kept verbatim so every URL that rendered
 *      before this change still renders - the public deployment's clips
 *      are sometimes keyed without an extension).
 *   3. S3-style presign markers (`X-Amz-Signature`/`X-Amz-Credential`
 *      query params - MinIO, R2, B2, AWS all emit these). CameraFrame's
 *      only callers pass camera CLIP urls, so a presigned object-storage
 *      URL here is a video by construction; this is the same reasoning
 *      that justified the original hostname check, applied to the
 *      protocol instead of one vendor's domain.
 *   4. An explicit `response-content-type=video/...` query hint.
 *
 * Exported for direct unit testing (tests/unit/camera-frame-video-url).
 *
 * @param url - URL string to check
 * @returns true if the URL is a video source
 */
export function isVideoUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    const pathname = parsed.pathname.toLowerCase();
    if (VIDEO_EXTENSIONS.some((ext) => pathname.endsWith(ext))) {
      return true;
    }
    // Legacy DO Spaces hostname sniff - kept so existing public-deployment
    // URLs keep rendering byte-identically.
    if (
      parsed.hostname.includes('.digitaloceanspaces.com') ||
      parsed.hostname.includes('.cdn.digitaloceanspaces.com')
    ) {
      return true;
    }
    // Endpoint-agnostic S3 presign markers (MinIO et al).
    if (
      parsed.searchParams.has('X-Amz-Signature') ||
      parsed.searchParams.has('X-Amz-Credential')
    ) {
      return true;
    }
    // Explicit content-type hint on the presigned response.
    const hintedType = parsed.searchParams.get('response-content-type');
    if (hintedType && hintedType.toLowerCase().startsWith('video/')) {
      return true;
    }
    return false;
  } catch {
    // Not parseable as an absolute URL (relative path / bare string):
    // fall back to the extension check only.
    const lower = url.toLowerCase().split('?')[0];
    return VIDEO_EXTENSIONS.some((ext) => lower.endsWith(ext));
  }
}

/**
 * CameraFrame: renders a camera clip or unavailability placeholder.
 *
 * @param props - CameraUrl prop
 */
export function CameraFrame({ cameraUrl }: Props) {
  return (
    <div className={styles.cameraFrameWrapper}>
      {cameraUrl && isVideoUrl(cameraUrl) ? (
        <video
          src={cameraUrl}
          autoPlay
          muted
          loop
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          aria-label="Camera clip"
        />
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--colors-text-tertiary)',
          }}
          aria-label="Camera clip unavailable"
        >
          <VideoCameraSlash size={24} weight="regular" />
          <span
            style={{
              fontFamily: 'var(--fonts-sans)',
              fontSize: '10px',
              letterSpacing: '0.04em',
            }}
          >
            Camera clip unavailable
          </span>
        </div>
      )}
    </div>
  );
}
